import React, { useEffect, useState, useCallback, useRef } from 'react';
import { usersAPI } from '../../api';
import { Loading, Pagination, Modal } from '../../components';
import { usePagination } from '../../hooks';
import { useToast } from '../../context/ToastContext';
import './Team.css';

const Team = () => {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const signatureInputRef = useRef(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetMode, setResetMode] = useState('generate'); // 'generate' | 'custom'
  const [customPassword, setCustomPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { newPassword, email }
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'TEAM_MEMBER'
  });
  const [submitting, setSubmitting] = useState(false);
  const pagination = usePagination(1, 10);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll({
        page: pagination.page,
        limit: pagination.limit
      });
      setUsers(response.data.data);
      pagination.updatePagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await usersAPI.create(formData);
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', role: 'TEAM_MEMBER' });
      fetchUsers();
      toast.success('User created successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await usersAPI.updateStatus(userId, !currentStatus);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openSignatureModal = (user) => {
    setSelectedUser(user);
    setShowSignatureModal(true);
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedUser) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.warning('Invalid file type. Please upload JPEG, PNG, or WebP for signature.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.warning('File too large. Maximum size is 2MB for signature.');
      return;
    }

    setUploadingSignature(true);

    try {
      const formData = new FormData();
      formData.append('signature', file);

      await usersAPI.uploadSignature(selectedUser._id, formData);
      fetchUsers();
      setShowSignatureModal(false);
      setSelectedUser(null);
      toast.success('Signature uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload signature');
    } finally {
      setUploadingSignature(false);
      if (signatureInputRef.current) {
        signatureInputRef.current.value = '';
      }
    }
  };

  const openResetModal = (user) => {
    setResetTarget(user);
    setResetMode('generate');
    setCustomPassword('');
    setResetResult(null);
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetTarget(null);
    setCustomPassword('');
    setResetResult(null);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;

    if (resetMode === 'custom') {
      if (!customPassword || customPassword.length < 6) {
        toast.warning('Password must be at least 6 characters');
        return;
      }
    }

    try {
      setResetting(true);
      const password = resetMode === 'custom' ? customPassword : null;
      const response = await usersAPI.resetPassword(resetTarget._id, password);
      const data = response.data?.data || {};
      setResetResult({
        newPassword: data.newPassword,
        email: data.email || resetTarget.email
      });
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!resetResult?.newPassword) return;
    try {
      await navigator.clipboard.writeText(resetResult.newPassword);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Could not copy. Please copy manually.');
    }
  };

  const handleDeleteSignature = async () => {
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to delete this signature?')) return;

    setUploadingSignature(true);

    try {
      await usersAPI.deleteSignature(selectedUser._id);
      fetchUsers();
      setShowSignatureModal(false);
      setSelectedUser(null);
      toast.success('Signature deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete signature');
    } finally {
      setUploadingSignature(false);
    }
  };

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="team-page">
      <div className="team-header">
        <div>
          <h1>Team Management</h1>
          <p>Manage users and their roles</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          Add User
        </button>
      </div>

      {loading ? (
        <Loading message="Loading users..." />
      ) : (
        <>
          <div className="table-container card">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Signature</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user._id}>
                      <td><strong>{user.name}</strong></td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge role-${user.role.toLowerCase()}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`btn btn-sm ${user.signature?.url ? 'btn-outline signature-uploaded' : 'btn-secondary'}`}
                          onClick={() => openSignatureModal(user)}
                        >
                          {user.signature?.url ? 'View/Edit' : 'Add'}
                        </button>
                      </td>
                      <td>
                        <span className={`status-indicator ${user.isActive ? 'active' : 'inactive'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className={`btn btn-sm ${user.isActive ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => handleToggleStatus(user._id, user.isActive)}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => openResetModal(user)}
                            title="Reset this user's password"
                          >
                            Reset Password
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={pagination.goToPage}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
          />
        </>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New User"
      >
        <form onSubmit={handleCreateUser}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select
              name="role"
              className="form-input"
              value={formData.role}
              onChange={handleChange}
            >
              <option value="TEAM_MEMBER">Team Member</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Signature Modal */}
      <Modal
        isOpen={showSignatureModal}
        onClose={() => { setShowSignatureModal(false); setSelectedUser(null); }}
        title={`Manage Signature - ${selectedUser?.name || ''}`}
      >
        <div className="signature-modal-content">
          <p className="signature-description">
            Upload a digital signature for {selectedUser?.name}. This signature will appear on PDF reports for samples collected by this user.
          </p>

          <div className="signature-preview-container">
            {selectedUser?.signature?.url ? (
              <img
                src={selectedUser.signature.url}
                alt={`${selectedUser.name}'s signature`}
                className="signature-preview-image"
              />
            ) : (
              <div className="signature-placeholder">
                <span>No signature uploaded</span>
              </div>
            )}
          </div>

          <div className="signature-actions">
            <input
              type="file"
              ref={signatureInputRef}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleSignatureUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => signatureInputRef.current?.click()}
              disabled={uploadingSignature}
            >
              {uploadingSignature ? 'Uploading...' : selectedUser?.signature?.url ? 'Change Signature' : 'Upload Signature'}
            </button>
            {selectedUser?.signature?.url && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteSignature}
                disabled={uploadingSignature}
              >
                Delete Signature
              </button>
            )}
          </div>
          <p className="signature-hint">Supported formats: JPEG, PNG, WebP. Max size: 2MB. Recommended: transparent background PNG</p>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={closeResetModal}
        title={`Reset Password — ${resetTarget?.name || ''}`}
      >
        {!resetResult ? (
          <form onSubmit={handleResetPassword}>
            <p className="reset-description">
              Reset the password for <strong>{resetTarget?.email}</strong>. Choose to auto-generate a strong password
              or set one manually. The user's current password will be replaced immediately.
            </p>

            <div className="reset-options">
              <label className={`reset-option ${resetMode === 'generate' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="resetMode"
                  value="generate"
                  checked={resetMode === 'generate'}
                  onChange={() => setResetMode('generate')}
                />
                <div>
                  <strong>Auto-generate</strong>
                  <p>Create a random 10-character password</p>
                </div>
              </label>

              <label className={`reset-option ${resetMode === 'custom' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="resetMode"
                  value="custom"
                  checked={resetMode === 'custom'}
                  onChange={() => setResetMode('custom')}
                />
                <div>
                  <strong>Set custom password</strong>
                  <p>Choose your own (min 6 characters)</p>
                </div>
              </label>
            </div>

            {resetMode === 'custom' && (
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="text"
                  className="form-input"
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                  minLength={6}
                  placeholder="Enter new password"
                  autoFocus
                />
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeResetModal}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={resetting}>
                {resetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        ) : (
          <div className="reset-result">
            <p className="reset-success-msg">
              Password for <strong>{resetResult.email}</strong> has been reset.
              Share this password with the user and ask them to change it after logging in.
            </p>
            <div className="password-display">
              <code className="password-value">{resetResult.newPassword}</code>
              <button type="button" className="btn btn-sm btn-primary" onClick={handleCopyPassword}>
                Copy
              </button>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={closeResetModal}>
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Team;
